import { formatDate } from "utils/dateFormatter";
import { dynamicModules } from "config/dynamicModules";
import { pathModuleType } from "config/urlConfig";

/**
 * Maps module data to appropriate component properties based on module type
 * Creates uniform data structures for different modules
 * 
 * @param {number} module_id - The module ID determining the mapping strategy
 * @param {Array} module_data - The module data to map
 * @param {string} routePrefix - Optional prefix for routes
 * @param {string} baseRoute - Base route path
 * @param {Object} logger - Optional logger object with logDebug and logError methods
 * @param {Object} options - Additional options for mapping
 * @param {boolean} options.isModuleSubdomain - Whether viewing on a module-specific subdomain
 * @returns {Array|Object} The mapped module items
 */
export const mapModuleProps = (module_id, module_data, routePrefix = "", baseRoute = "", logger = null, options = {}) => {
	// Default logger functions if none is provided
	const logDebug = logger?.logDebug || console.log;
	
	// Extract isModuleSubdomain from options
	const { isModuleSubdomain = false } = options;
	
	// Check if we're in DEV mode using pathModuleType
	const isPathModuleMode = Boolean(pathModuleType);
	
	if (!Array.isArray(module_data)) return [];
	let items = [];
	
	/**
	 * Helper function to generate the appropriate link based on context
	 * Creates absolute or relative URLs depending on subdomain usage
	 */
	const createModuleLink = (route, uniqueUrl, targetSubdomain = null) => {
		// Standard values for CTA link creation
		if (!uniqueUrl) {
			// For missing uniqueUrl we create a link to the overview page
			const orgUrl = window.location.pathname.split('/')[1] || 'engbers-bau';
			
			// In subdomain mode: /{orgUrl}
			if (isModuleSubdomain || isPathModuleMode) {
				return `/${orgUrl}`;
			}
			// In normal website context: /{route}
			return `/${route}`;
		}
		
		// Check if we're already on the target subdomain
		const currentSubdomain = window.location.hostname.split('.')[0];
		const effectiveTargetSubdomain = (currentSubdomain === targetSubdomain) ? null : targetSubdomain;
		
		// Extract organization from URL
		const orgUrl = window.location.pathname.split('/')[1] || 'engbers-bau';
		
		// For module subdomains or DEV mode with pathModuleType
		if (isModuleSubdomain || isPathModuleMode) {
			// If linking to different subdomain, create absolute URL with direct format
			if (effectiveTargetSubdomain) {
				return `https://${effectiveTargetSubdomain}.canou.io/${orgUrl}/${uniqueUrl}`;
			}
			// For same subdomain or DEV mode: use direct path (NO module segment)
			return `/${orgUrl}/${uniqueUrl}`;
		}
		// For normal website context
		else {
			return `/${route}/${uniqueUrl}`;
		}
	};
	
	// Standardisierte Mapping-Funktion, die für alle Module verwendet werden kann
	const mapStandardModuleItem = (item, moduleRoute, defaultCtaText, targetSubdomain = null) => {
		// Basisdaten immer mappen
		const mappedItem = {
			id: item.id,
			title: item.title,
			short_desc: item.short_desc,
			desc: item.desc,
			media: item.medias || item.media,
			unique_url: item.unique_url
		};
		
		// Immer CTA-Daten erzeugen, wenn unique_url vorhanden
		if (item.unique_url) {
			mappedItem.cta_text = item.cta_text || defaultCtaText;
			mappedItem.cta_link = createModuleLink(moduleRoute, item.unique_url, targetSubdomain);
		}
		
		return mappedItem;
	};
	
	switch (module_id) {
		case 9999: // Checkout
			items = module_data.map((item) => ({
				data: item.data,
			}));
			return items;
		case 4610: // Testimonials
			items = module_data.map((item) => ({
				title: item.author,
				short_desc: item.position,
				desc: item.quote,
				image: item.medias?.[0]?.mediaUrl,
				image_alt: `${item.author} - ${item.position}` // Improved accessibility with meaningful alt text
			}));
			return items;

		case 2610: // Job Offers
			// Dynamically get the route from dynamicModules
			const jobModule = dynamicModules.find(m => m.module_id === 2610);
			const jobRoute = jobModule ? jobModule.route : "stellen";
			
			items = module_data.map((item) => {
				// Nutze die standardisierte Mapping-Funktion als Basis
				const baseItem = mapStandardModuleItem(item, jobRoute, "Zur Stelle", "jobs");
				
				// Füge jobspezifische Eigenschaften hinzu
				return {
					...baseItem,
					desc: item.short_desc,
					short_desc: null,
					footer: item.immediately 
						? "Einstellungsdatum: sofort" 
						: item.valid_until 
							? "Einstellungsdatum: " + formatDate(item.valid_until) 
							: null,
					// Structured data for SEO
					structuredData: {
						"@context": "https://schema.org",
						"@type": "JobPosting",
						"title": item.title,
						"description": item.short_desc,
						"datePosted": new Date().toISOString().split('T')[0],
						"validThrough": item.valid_until || "",
						"employmentType": item.employment_type?.name || "FULL_TIME",
						"hiringOrganization": {
							"@type": "Organization",
							"name": item.address_title || "Company"
						},
						"jobLocation": {
							"@type": "Place",
							"address": {
								"@type": "PostalAddress",
								"streetAddress": item.address_street || "",
								"addressLocality": item.address_location || "",
								"postalCode": item.address_postal_code || "",
								"addressRegion": item.address_region || "",
								"addressCountry": item.address_country || "DE"
							}
						}
					}
				};
			});
			return items;

		case 9010: // Forms
			// Dynamically get the route from dynamicModules
			const formModule = dynamicModules.find(m => m.module_id === 9010);
			const formRoute = formModule ? formModule.route : "formulare";
			
			// Standardisierte Verarbeitung für Formulare, analog zu anderen Modultypen
			items = module_data.map((item) => {
				console.log("item item item", item);
				// Nutze die standardisierte Mapping-Funktion
				const baseItem = mapStandardModuleItem(item, formRoute, "Zum Formular", "forms");
				
				// Füge formularspezifische Eigenschaften hinzu
				return {
					...baseItem,
					type: item.type?.title?.toLowerCase() || "stepper",
					// Add metadata for better organization
					metadata: {
						created_at: item.created_at,
						updated_at: item.updated_at,
						formType: item.type?.title
					}
				};
			});
			return items;

		case 1325: // Links
			// Dynamically get the route from dynamicModules
			const linkModule = dynamicModules.find(m => m.module_id === 1325);
			const linkRoute = linkModule ? linkModule.route : "links";
			
			items = module_data.map((item) => {
				// Use the standardized mapping function
				const baseItem = mapStandardModuleItem(item, linkRoute, "Link öffnen", "links");
				
				// Add link-specific properties
				return {
					...baseItem,
					data: { 
						url: item.url
					}
					
				};
			});
			return items;

		case 10010: // FAQs
			items = module_data.map((item, index) => ({
				title: item.title,
				short_desc: item.short_desc,
				desc: item.desc,
				// Structured data for SEO and accessibility
				structuredData: {
					"@context": "https://schema.org",
					"@type": "Question",
					"name": item.title,
					"acceptedAnswer": {
						"@type": "Answer",
						"text": item.desc || item.short_desc
					}
				},
				// Accessibility attributes
				attributes: {
					"id": `faq-${index}`,
					"aria-expanded": "false"
				}
			}));
			return items;
		
		case 4910: // Agendas
			items = module_data.map((agenda) => ({
				title: agenda.title,
				short_desc: agenda.short_desc,
				desc: agenda.desc || "",
				// Format timestamp in format "10:00 - 11:30"
				time: agenda.start_time && agenda.end_time ? 
					`${agenda.start_time.substring(0, 5)} - ${agenda.end_time.substring(0, 5)}` : 
					null,
				start_time: agenda.start_time ? agenda.start_time.substring(0, 5) : null,
				end_time: agenda.end_time ? agenda.end_time.substring(0, 5) : null,
				// All speaker information
				speakers: agenda?.speakers?.map(speaker => ({
					name: speaker?.contact_person ? 
						`${speaker?.contact_person?.title || ''} ${speaker?.contact_person?.name || ''} ${speaker?.contact_person?.last_name || ''}`.trim() : 
						null,
					organization: speaker?.contact_person?.company_name || speaker?.contact_person?.company_position,
					image: speaker?.contact_person?.medias?.[0] ?? "/assets/img/blank-profile-picture.webp",
					image_alt: speaker?.contact_person ? 
						`${speaker?.contact_person?.title || ''} ${speaker?.contact_person?.name} ${speaker?.contact_person?.last_name || ''}`.trim() : 
						"Speaker",
					link: null
				})) || [],
				// Venue information
				location: agenda.stage?.title || agenda.location?.name,
				start_date: agenda.start_date,
				end_date: agenda.end_date,
				// Media files
				image: agenda.medias?.[0],
				// Event tracking
				eventId: agenda.id,
				structuredData: {
					"@context": "https://schema.org",
					"@type": "Event",
					"name": agenda.title,
					"startDate": agenda.start_date,
					"endDate": agenda.end_date,
					"location": {
						"@type": "Place",
						"name": agenda.stage?.title || agenda.location?.name || ""
					},
					"description": agenda.short_desc || agenda.desc || ""
				}
			}));
			return items;

		case 4710: // Locations
			const location = module_data?.[0];
			if (!location) return {};
			
			return {
				title: location.title,
				short_desc: location.short_desc,
				desc: location.desc,
				media: location.medias || location.media,
				address: {
					street: location.street,
					postalCode: location.postalcode,
					city: location.city,
					region: location.region,
					country: location.country
				},
				coordinates: location.coordinates,
				// Structured data for location
				structuredData: {
					"@context": "https://schema.org",
					"@type": "Place",
					"name": location.title,
					"description": location.short_desc,
					"address": {
						"@type": "PostalAddress",
						"streetAddress": location.street,
						"addressLocality": location.city,
						"postalCode": location.postalcode,
						"addressRegion": location.region,
						"addressCountry": location.country
					},
					"geo": location.coordinates ? {
						"@type": "GeoCoordinates",
						"latitude": location.coordinates.lat,
						"longitude": location.coordinates.lng
					} : undefined
				}
			};

		case 1610: // Events
			logDebug("Event module raw data:", module_data);
			const eventModule = dynamicModules.find(m => m.module_id === 1610);
			const eventRoute = eventModule?.route || "";

			
			
			items = module_data.map((item) => {
				console.log(item);
				// Standardbasis für Events nutzen
				const baseItem = mapStandardModuleItem(item, eventRoute, "Zur Veranstaltung");
				const location = item.location || {};
				
				// Helper function to check if event is currently live
				const isEventLive = () => {
					// Check if we have the necessary date information
					if (!item.date ) return false;
					
					// Parse dates into JavaScript Date objects and get timestamps
					const currentTimestamp = new Date().getTime();
					const startTimestamp = new Date(item.date).getTime();
					const endTimestamp = item.end_date ? new Date(item.end_date).getTime() : null;
					
					// Check if current time is between start and end date
					// Or exactly on the same day as start date if no end date is provided
					if (endTimestamp) {
						return currentTimestamp >= startTimestamp && currentTimestamp < endTimestamp;
					} else {
						// If no end date, check if current date is on the same day as start date
						// Create new Date objects to remove time portion and compare only dates
						const currentDateOnly = new Date(new Date().setHours(0, 0, 0, 0));
						const startDateOnly = new Date(new Date(item.date).setHours(0, 0, 0, 0));
						return currentDateOnly.getTime() === startDateOnly.getTime();
					}
				};
				
				// Build feature list items, conditionally including the live badge
				const featureListItems = [
					{
						value: formatDate(item.date),
						icon: "calendarAlt",
						label: "Datum"
					},
					{
						value: formatDate(item.date, { year: undefined, month: undefined, day: undefined, hour: 'numeric', minute: 'numeric' }),
						icon: "clock",
						label: "Uhrzeit"
					},
					{
						value: location?.city && location?.title ? `${location.city} | ${location.title}` : (location?.city || location?.title || ""),
						icon: "map",
						label: "Ort",
						link: location?.street ? 
							`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${location.street}, ${location.postalcode} ${location.city}`)}` : 
							null
					}
				];
				
				// Only add live badge if event is currently happening
				if (isEventLive()) {
					featureListItems.push({
						value: `
							<div class="live-badge-wrapper position-relative rounded px-3 py-1">
								<div class="live-badge"></div>
								<div>Jetzt Live</div>
							</div>
						`,
					});
				}
				
				return {
					...baseItem,
					col_width: 12,
					simple_feature_list: {
						title: null,
						items: featureListItems
					},
					list: {
						title: null,
						items: [
							location?.title && {
								title: location.city,
								value: `Live in <strong>${location.title}</strong>`,
								icon: "locationDot",
								label: "Veranstaltungsort"
							},
							{
								title: "Exklusivität",
								value: "Max. <strong>100 Teilnehmer*innen</strong>",
								icon: "users",
								label: "Teilnehmerzahl"
							},
							{
								title: "Impulse, spannende Unternehmenseinblicke & Use Cases aus der Branche",
								value: `<strong>${item?.selectable_agendas?.length ?? 0}</strong> Fokusthemen mit Keynote, Deep Dives und Panels`,
								icon: "microphone",
								label: "Programm"
							}
						].filter(Boolean) // Remove any undefined items
					},
					speakers: item.speakers?.map(speaker => ({
						title: `${speaker?.name || ''} ${speaker?.last_name || ''}`.trim(),
						desc: speaker?.company_position ? 
							(speaker?.company_name ? `${speaker?.company_position} | ${speaker?.company_name}` : speaker?.company_position) : 
							speaker?.company_name || '',
						media: speaker?.medias ?? "/assets/img/blank-profile-picture.webp"
					})) || [],
					// Structured data for event
					structuredData: {
						"@context": "https://schema.org",
						"@type": "Event",
						"name": item.title,
						"description": item.short_desc || item.short_content || "",
						"startDate": item.date,
						"eventStatus": "https://schema.org/EventScheduled",
						"location": location?.title ? {
							"@type": "Place",
							"name": location.title,
							"address": {
								"@type": "PostalAddress",
								"streetAddress": location.street || "",
								"addressLocality": location.city || "",
								"postalCode": location.postalcode || "",
								"addressCountry": location.country || "DE"
							}
						} : undefined,
						"image": item.medias?.[0]?.mediaUrl || "",
						"performer": item.speakers?.map(speaker => ({
							"@type": "Person",
							"name": `${speaker?.name || ''} ${speaker?.last_name || ''}`.trim()
						})) || []
					}
				};
			});
			
			logDebug("Mapped event items with subsections:", items);
			return items;
			
		default: 
			// Für unbekannte Module: Standardisierte Mapping-Funktion verwenden
			const defaultModule = dynamicModules.find(m => m.module_id === module_id);
			const defaultRoute = defaultModule ? defaultModule.route : "";
			
			if (defaultRoute) {
				return module_data.map(item => mapStandardModuleItem(item, defaultRoute, "Details"));
			}
			
			return items;
	}
};

